import { union, difference } from 'lib';
import { isDisabledFilter } from 'store/helpers/filterHelpers';
import { getRequestLanguages, isNativePlatform, magicNumbers } from 'utils';

const contentAlarmApiPath = {
  news: '/news/posts',
  newspaper: '/newspaper/posts',
  bale: '/iranian-platforms/posts',
  rubika: '/iranian-platforms/posts',
  eitaa: '/iranian-platforms/posts',
  virasty: '/virasty/posts',
  instagram: '/instagram/posts',
  twitter: '/twitter/posts',
  telegram: '/telegram/posts',
  linkedin: '/linkedin/posts',
};

const reportingAlarmApiPath = {
  news: '/news/info',
  instagram: '/instagram/info',
  twitter: '/twitter/info',
  telegram: '/telegram/info',
  virasty: '/virasty/info',
};

const nativePlatforms = ['bale', 'rubika', 'eitaa'];

const getSearchParams = (alarmState, _platform) => {
  const filters = alarmState?.filters[_platform];
  const contentBasedFilter = filters?.contentBasedFilter?.selectedObjects;
  const sentimentFilter = filters?.sentimentFilter?.selectedValues;

  const subCategories = isDisabledFilter('contentCategoryFilter', filters)
    ? undefined
    : Object.values(filters?.contentCategoryFilter?.selectedCategories);

  const selectedTagId =
    filters?.resourceCategoryFilter?.filterType === 'include'
      ? 'tag_id'
      : 'not_tag_id';

  const selectedResourceId =
    filters?.resourcesFilter?.selectedValue === 'include'
      ? 'resource_ids'
      : 'not_resource_ids';

  const messageFields = alarmState.messageFields?.[_platform] || [];

  let fields = messageFields;
  if (
    nativePlatforms.includes(_platform) &&
    alarmState.contentType === 'subject' &&
    !messageFields?.includes('platform')
  ) {
    fields = [...messageFields, 'platform'];
  }

  const allPlatformParams = {
    api_path:
      alarmState.type === 'content'
        ? contentAlarmApiPath[_platform]
        : reportingAlarmApiPath[_platform],
    query: alarmState?.selectedSubjectId,
    fields,
    resource_influence_categories: alarmState.filters[_platform]
      ?.contentBasedFilter?.selectedObjects?.influenceRate?.active
      ? alarmState.filters[_platform]?.contentBasedFilter?.selectedObjects
          ?.influenceRate.selectedIds
      : undefined,
    langs: isDisabledFilter('languageFilter', filters)
      ? undefined
      : getRequestLanguages(filters),
    categories: isDisabledFilter('contentCategoryFilter', filters)
      ? undefined
      : Object.keys(filters?.contentCategoryFilter?.selectedCategories),
    sub_categories:
      subCategories?.length > 0 ? subCategories.flat(1) : undefined,
    resource_categories: isDisabledFilter('subjectFilter', filters)
      ? undefined
      : filters?.subjectFilter?.selectedIds,
    [selectedTagId]: isDisabledFilter('resourceCategoryFilter', filters)
      ? undefined
      : filters?.resourceCategoryFilter?.selectedValue,
    [selectedResourceId]: isDisabledFilter('resourcesFilter', filters)
      ? undefined
      : Object.keys(filters?.resourcesFilter?.selectedItems),
    sentiments: isDisabledFilter('sentimentFilter', filters)
      ? undefined
      : Object.keys(sentimentFilter).filter(
          (sentiment) => sentimentFilter?.[sentiment] !== false
        ),
    min_like: isDisabledFilter('like', filters)
      ? undefined
      : contentBasedFilter?.like?.min,
    max_like: isDisabledFilter('like', filters)
      ? undefined
      : contentBasedFilter?.like?.max,
    min_reactions: isDisabledFilter('reaction', filters)
      ? undefined
      : contentBasedFilter?.reaction?.min,
    max_reactions: isDisabledFilter('reaction', filters)
      ? undefined
      : contentBasedFilter?.reaction?.max,
    min_comment: isDisabledFilter('comment', filters)
      ? undefined
      : contentBasedFilter?.comment?.min,
    max_comment: isDisabledFilter('comment', filters)
      ? undefined
      : contentBasedFilter?.comment?.max,
    min_copy: isDisabledFilter('copy', filters)
      ? undefined
      : contentBasedFilter?.copy?.min ?? undefined,
    max_copy: isDisabledFilter('copy', filters)
      ? undefined
      : contentBasedFilter?.copy?.max ?? undefined,
    min_view: isDisabledFilter('view', filters)
      ? undefined
      : contentBasedFilter?.view?.min ?? undefined,
    max_view: isDisabledFilter('view', filters)
      ? undefined
      : contentBasedFilter?.view?.max ?? undefined,
    platforms: isNativePlatform({ platform: _platform })
      ? [`${_platform}_channel_post`]
      : undefined,
    m3_age: isDisabledFilter('ageFilter', filters)
      ? undefined
      : filters?.ageFilter?.selectedIds,
    gender: isDisabledFilter('genderFilter', filters)
      ? undefined
      : filters?.genderFilter?.selectedIds,
  };

  // اسم فیلد فیلتر نوع محتوا و نوع منبع شباهت دارن ولی مقادیرشون با توجه به پلتفرم فرق داره
  if (_platform === 'news') {
    allPlatformParams.media_types = isDisabledFilter(
      'resourceTypeFilter',
      filters
    )
      ? undefined
      : alarmState?.filters[_platform]?.resourceTypeFilter?.selectedIds ??
        undefined;
    allPlatformParams.resource_countries =
      !isDisabledFilter('countryFilter', filters) &&
      filters?.countryFilter?.selectedIds?.length > 0
        ? filters?.countryFilter?.selectedIds
        : undefined;
  } else if (filters?.mediaTypeFilter?.selectedIds?.includes('other')) {
    allPlatformParams.media_types = isDisabledFilter('mediaTypeFilter', filters)
      ? undefined
      : [
          'article',
          'document',
          'poll',
          ...(filters?.mediaTypeFilter?.selectedIds?.filter(
            (mediaType) => mediaType !== 'other'
          ) || []),
        ];
  } else {
    allPlatformParams.media_types = isDisabledFilter('mediaTypeFilter', filters)
      ? undefined
      : alarmState?.filters[_platform]?.mediaTypeFilter?.selectedIds;
  }

  // اگر این موارد وجود داشتن و ارایه انها خالی بود حذف بشن و با مقدار ارایه خالی ارسال نشن
  if (!allPlatformParams?.categories?.length)
    delete allPlatformParams.categories;
  if (!allPlatformParams?.sub_categories?.length)
    delete allPlatformParams.sub_categories;
  if (!allPlatformParams?.resource_ids?.length)
    delete allPlatformParams.resource_ids;
  if (!allPlatformParams?.not_resource_ids?.length)
    delete allPlatformParams.not_resource_ids;
  if (!allPlatformParams?.gender?.length) delete allPlatformParams.gender;
  if (!allPlatformParams?.m3_age?.length) delete allPlatformParams.m3_age;
  if (!allPlatformParams?.media_types?.length)
    delete allPlatformParams.media_types;
  if (!allPlatformParams?.media_type?.length)
    delete allPlatformParams.media_type;

  // مشخص کردن فیلتر عمومی فقط مطالب دست اول
  if (
    _platform !== 'instagram' &&
    alarmState.publicFilter.includes('onlyOriginalContents')
  ) {
    allPlatformParams.source = 'original';
  }

  // مشخص کردن فیلتر عمومی عدم ارسال مطالب تبلیغاتی
  if (
    _platform !== 'news' &&
    alarmState.publicFilter.includes('ignoreAdvertisment')
  ) {
    allPlatformParams.min_advert_noise = 0;
    allPlatformParams.max_advert_noise = 0.15;
  }

  // مشخص کردن فیلترهای پیشفرض بسترها در صورت فعال شدن بروزرسانی اطلاعات مطالب
  const platformDefaultUpdataFields = {
    instagram: ['like_count', 'comment_count'],
    news: ['copy_count'],
    telegram: ['view_Count'],
    twitter: ['like_count', 'copy_count'],
    linkedin: ['share_count'],
    virasty: ['reactions_count', 'repost_count'],
  };
  const fieldsToUpdate = platformDefaultUpdataFields[_platform];
  if (fieldsToUpdate) {
    if (
      alarmState.advancedSettingsUpdate &&
      alarmState.selectedChannelType === 'telegram'
    ) {
      allPlatformParams.fields = union(
        allPlatformParams.fields,
        fieldsToUpdate
      );
    } else {
      allPlatformParams.fields = difference(
        allPlatformParams.fields,
        fieldsToUpdate
      );
    }
  }

  return allPlatformParams;
};

export const getPlatform = (_platform) => {
  if (_platform?.includes('newspaper')) return 'newspaper';
  if (_platform?.includes('news')) return 'news';
  if (_platform?.includes('instagram')) return 'instagram';
  if (_platform?.includes('telegram')) return 'telegram';
  if (_platform?.includes('twitter')) return 'twitter';
  if (_platform?.includes('iranian')) return 'nativePlatforms';
  if (_platform?.includes('linkedin')) return 'linkedin';
  if (_platform?.includes('virasty')) return 'virasty';
  if (_platform?.includes('bazaar')) return 'cafeBazaar';
};

export const prepareParams = (alarmState, payload) => {
  const settings = [];
  const isTelegram = alarmState.selectedChannelType === 'telegram';

  if (alarmState.advancedSettingsUpdate && isTelegram) {
    settings.push({
      type: 'update_entities',
      is_active: alarmState.advancedSettingsUpdate,
    });
  }

  if (alarmState.advancedSettingsDelete && isTelegram) {
    settings.push({
      type: 'delete_message',
      is_active: alarmState.advancedSettingsDelete,
      extra: alarmState.advancedSettingsDeleteIDs,
    });
  }

  const advancedSettings = {
    enable:
      isTelegram &&
      (alarmState.advancedSettingsUpdate || alarmState.advancedSettingsDelete),
    settings: settings,
  };

  let type = magicNumbers.ALARM_CONTENT_SELECTED_SUBJECT;
  if (alarmState.alarmSource === 'appStore') {
    type = magicNumbers.ALARM_CONTENT_SELECTED_SUBJECT;
  } else if (alarmState.type === 'content') {
    if (alarmState.contentType === 'resource')
      type = magicNumbers.ALARM_CONTENT_SELECTED_RESOURCE;
    else type = magicNumbers.ALARM_CONTENT_SELECTED_SUBJECT;
  } else if (alarmState.reportType === 'statistical')
    type = magicNumbers.ALARM_REPORTING_STATISTICAL;
  else if (alarmState.reportType === 'negativeIssues')
    type = magicNumbers.ALARM_REPORTING_NEGATIVE_ISSUES;
  else if (alarmState.reportType === 'trending')
    type = magicNumbers.ALARM_REPORTING_TRENDING;

  const params = {
    ...payload,
    type,
    title: alarmState.title,
    is_active: true,
    internal: true,
    receivers: [alarmState.selectedChannelId],
    advanced_settings: advancedSettings.enable ? advancedSettings : undefined,
  };

  if (
    alarmState.type === 'content' ||
    alarmState.reportType === 'statistical'
  ) {
    params.search_params = alarmState?.selectedPlatforms?.map((_platform) =>
      getSearchParams(alarmState, _platform)
    );
  }

  if (alarmState.reportType === 'negativeIssues') {
    params.search_params = [
      {
        query: alarmState?.selectedSubjectId,
      },
    ];
  }

  if (alarmState.type === 'reporting') {
    params.notify_at = alarmState.selectedHour;

    if (alarmState.durationType === 'repeatedDays') {
      params.notify_duration = parseInt(alarmState.durationValue?.[0]);
    } else if (alarmState.durationType === 'everyDay') {
      params.notify_duration = 1;
    } else if (alarmState.durationType === 'weekDays') {
      params.notify_days = alarmState.durationValue;
    }
  }

  if (alarmState.hasActiveAiSummary) {
    const aiSummarySettings = {
      time: alarmState.selectedHour,
    };
    if (alarmState.durationType === 'everyDay') {
      aiSummarySettings.interval = 'daily';
    } else if (alarmState.durationType === 'weekDays') {
      aiSummarySettings.interval = 'weekly';
      aiSummarySettings.days = alarmState.durationValue;
    }
    params.ai_summary_settings = aiSummarySettings;
  }

  if (alarmState.alarmSource === 'appStore') {
    params.search_params = [
      {
        api_path: '/cafebazaar/comments',
        fields: [
          'time',
          'title',
          'url',
          'raw_content',
          'rate',
          'resource_user',
          'application_name',
        ],
        application_ids:
          alarmState.selectedAppIds.length > 0
            ? alarmState.selectedAppIds
            : undefined,
        rates: isDisabledFilter('ratingFilter', alarmState.filters.cafeBazaar)
          ? undefined
          : alarmState.filters.cafeBazaar.ratingFilter.selectedIds,
      },
    ];
  }

  return params;
};
